import { Logger } from './Logger'
import { SyslogLevel, LogModule, LogComponent } from './loggerTypes'
import type { LoggerClientConfig } from './loggerTypes'

const DEFAULT_CONFIG: LoggerClientConfig = {
  module: LogModule.Farmgrid,
  component: LogComponent.frontend,
  logLevel: SyslogLevel.info,
  commonInfoKeys: [],
}

/**
 * Derive a short human-readable label from an import.meta object or file path string.
 * Returns the last 2 path segments, e.g. 'hooks/app-form.tsx'.
 */
function extractModuleLabel(moduleLike: unknown): string {
  if (!moduleLike) return 'unknown'

  if (typeof moduleLike === 'object' && moduleLike !== null) {
    const maybeUrl = (moduleLike as { url?: unknown }).url
    if (typeof maybeUrl === 'string') return extractModuleLabel(maybeUrl)
  }

  if (typeof moduleLike !== 'string') return 'unknown'

  try {
    const url = moduleLike.includes('://') ? new URL(moduleLike) : undefined
    const pathname = url?.pathname ?? moduleLike
    const parts = pathname.split('/').filter(Boolean)
    if (parts.length === 0) return pathname
    return parts.slice(-2).join('/')
  } catch {
    return moduleLike
  }
}

/**
 * AppLogger is the application-level singleton that replaces LogManager.
 *
 * All loggers it creates hold a live reference to `currentConfig`, so calling
 * configure() immediately updates every existing logger instance — no re-import
 * or re-creation needed at call sites.
 */
class AppLoggerSingleton {
  private currentConfig: LoggerClientConfig = { ...DEFAULT_CONFIG }

  /**
   * Returns a Logger bound to the given module (typically pass `import.meta`).
   * The file path is extracted and used as the component label.
   */
  getLogger(moduleLike: unknown): Logger {
    const moduleLabel = extractModuleLabel(moduleLike)
    return new Logger(() => this.currentConfig).cloneWithComponent(moduleLabel)
  }

  /**
   * Update global logger config. Called once by ConfigContext after YAML loads.
   * All existing loggers immediately reflect the new config.
   */
  configure(config: LoggerClientConfig): void {
    this.currentConfig = { ...config }
  }
}

export const AppLogger = new AppLoggerSingleton()
